import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  LayoutDashboard, Package, Users, LogOut, Loader2, Euro, ShoppingCart,
  TrendingUp, Mail, Lock, BellRing, Send, PackagePlus, Trash2, Plus, X, Link2, Copy, Check,
  CheckCircle2, ExternalLink, RefreshCw, Search,
} from "lucide-react";
import { adminLogin, getAdminStats, getAdminOrders, getAdminPaidOrders, getAdminSubscribers, getAdminWaitlist, notifyWaitlist, getAdminProducts, createAdminProduct, deleteAdminProduct, createPaymentLink } from "../lib/api";
import { useToast } from "../hooks/use-toast";

const PRODUCT_NAMES = {
  "copper-peptide-serum-30ml": "Copper Peptide Complex 03",
  "color-lip-tint": "Color Lip Tint",
  "lip-gloss": "Lip Gloss",
};

const STATUS_STYLES = {
  paid: "bg-[#E4F1E9] text-[#3C7A57]",
  pending: "bg-[#FBEFD9] text-[#9A7420]",
  failed: "bg-[#F7E0DA] text-[#B4553F]",
  expired: "bg-[#EDE7E4] text-[#7A6C66]",
};

const Login = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { token } = await adminLogin(password);
      localStorage.setItem("lelune_admin", token);
      onLogin(token);
    } catch {
      setError("Onjuist wachtwoord / Invalid password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FBF5F0] px-5">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-3xl border border-[#EEDDD3] p-9 shadow-xl">
        <Link to="/" className="block font-serif text-3xl tracking-brand text-[#3A2C28] text-center">LÉLUNE</Link>
        <p className="mt-1 text-[11px] tracking-label uppercase text-[#B4715C] text-center">Admin</p>
        <div className="mt-8 relative">
          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#B09C93]" />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Wachtwoord"
            className="w-full h-12 pl-11 pr-4 rounded-full border border-[#E0CDC2] bg-[#FBF5F0] text-sm focus:outline-none focus:border-[#B4715C]"
          />
        </div>
        {error && <p className="mt-3 text-[12px] text-[#C8604B] text-center">{error}</p>}
        <button
          type="submit"
          disabled={busy}
          className="mt-6 w-full h-12 rounded-full bg-[#B4715C] text-white text-[12px] tracking-label uppercase hover:bg-[#9E5F4B] disabled:opacity-70 flex items-center justify-center gap-2"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Inloggen
        </button>
        <p className="mt-4 text-[11px] text-center text-[#A38F86]">Demo wachtwoord: lelune-admin-2025</p>
      </form>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-white rounded-2xl border border-[#EEDDD3] p-6">
    <div className="flex items-center justify-between">
      <span className="text-[11px] tracking-label uppercase text-[#8A756C]">{label}</span>
      <span className="h-9 w-9 rounded-full bg-[#F4E6DE] flex items-center justify-center text-[#B4715C]">
        <Icon className="h-4 w-4" />
      </span>
    </div>
    <p className="mt-4 font-serif text-4xl text-[#3A2C28]">{value}</p>
  </div>
);

const Dashboard = ({ token, onLogout }) => {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [subs, setSubs] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [products, setProducts] = useState([]);
  const [notifying, setNotifying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [paidOrders, setPaidOrders] = useState([]);
  const [paidTotal, setPaidTotal] = useState(0);
  const [paidLoading, setPaidLoading] = useState(false);
  const [paidLoaded, setPaidLoaded] = useState(false);
  const [orderSearch, setOrderSearch] = useState("");
  const [paidSearch, setPaidSearch] = useState("");
  const { toast } = useToast();

  const matchQuery = (q, ...fields) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return fields.some((f) => (f || "").toLowerCase().includes(s));
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Orders first: it reconciles pending payments against Stripe, so stats read fresh data after.
      const o = await getAdminOrders(token);
      const [s, sub, wl, pr] = await Promise.all([
        getAdminStats(token),
        getAdminSubscribers(token),
        getAdminWaitlist(token),
        getAdminProducts(token),
      ]);
      setStats(s);
      setOrders(o.orders || []);
      setSubs(sub.subscribers || []);
      setWaitlist(wl.waitlist || []);
      setProducts(pr.products || []);
    } catch {
      onLogout();
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => { load(); }, [load]);

  const loadPaid = useCallback(async (force = false) => {
    if (paidLoaded && !force) return;
    setPaidLoading(true);
    try {
      const res = await getAdminPaidOrders(token);
      setPaidOrders(res.paid_orders || []);
      setPaidTotal(res.total || 0);
      setPaidLoaded(true);
    } catch {
      toast({ title: "LÉLUNE", description: "Kon betaalde orders niet laden." });
    } finally {
      setPaidLoading(false);
    }
  }, [token, paidLoaded, toast]);

  useEffect(() => { if (tab === "paid") loadPaid(); }, [tab, loadPaid]);

  const notifyAll = async () => {
    setNotifying(true);
    try {
      const res = await notifyWaitlist(token);
      toast({ title: "LÉLUNE", description: `${res.notified} e-mails verzonden (GEMOCKT).` });
      await load();
    } catch {
      toast({ title: "LÉLUNE", description: "Er ging iets mis." });
    } finally {
      setNotifying(false);
    }
  };

  const nav = [
    { id: "overview", label: "Overzicht", icon: LayoutDashboard },
    { id: "products", label: "Producten", icon: PackagePlus },
    { id: "orders", label: "Bestellingen", icon: Package },
    { id: "paid", label: "Betaald", icon: CheckCircle2 },
    { id: "subscribers", label: "Nieuwsbrief", icon: Users },
    { id: "waitlist", label: "Wachtlijst", icon: BellRing },
  ];

  const emptyForm = { name: "", price: "", category: "Lippen", subtitle: "", description: "", image: "", preorder: false, tag: "NEW" };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const submitProduct = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price) return;
    setSaving(true);
    try {
      await createAdminProduct(token, { ...form, price: parseFloat(form.price) });
      toast({ title: "LÉLUNE", description: `Product "${form.name}" toegevoegd.` });
      setForm(emptyForm);
      await load();
      setTab("products");
    } catch {
      toast({ title: "LÉLUNE", description: "Toevoegen mislukt." });
    } finally {
      setSaving(false);
    }
  };

  const removeProduct = async (pid) => {
    try {
      await deleteAdminProduct(token, pid);
      toast({ title: "LÉLUNE", description: "Product verwijderd." });
      await load();
    } catch {
      toast({ title: "LÉLUNE", description: "Verwijderen mislukt." });
    }
  };

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("nl-NL", { dateStyle: "medium", timeStyle: "short" }) : "-");

  return (
    <div className="min-h-screen bg-[#FBF5F0] flex">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 border-r border-[#E7D3C8] bg-white hidden md:flex flex-col">
        <div className="h-[72px] flex items-center px-6 border-b border-[#E7D3C8]">
          <span className="font-serif text-2xl tracking-brand text-[#3A2C28]">LÉLUNE</span>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {nav.map((n) => (
            <button
              key={n.id}
              onClick={() => setTab(n.id)}
              className={`w-full flex items-center gap-3 px-4 h-11 rounded-xl text-sm transition-colors ${
                tab === n.id ? "bg-[#F4E6DE] text-[#B4715C]" : "text-[#5A4740] hover:bg-[#FBF5F0]"
              }`}
            >
              <n.icon className="h-4 w-4" /> {n.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-[#E7D3C8] space-y-1">
          <Link to="/" className="w-full flex items-center gap-3 px-4 h-11 rounded-xl text-sm text-[#5A4740] hover:bg-[#FBF5F0]">
            Naar website
          </Link>
          <button onClick={onLogout} className="w-full flex items-center gap-3 px-4 h-11 rounded-xl text-sm text-[#C8604B] hover:bg-[#F7E0DA]">
            <LogOut className="h-4 w-4" /> Uitloggen
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <header className="h-[72px] flex items-center justify-between px-6 border-b border-[#E7D3C8] bg-white">
          <h1 className="font-serif text-2xl text-[#3A2C28] capitalize">{nav.find((n) => n.id === tab)?.label}</h1>
          <div className="flex items-center gap-3 md:hidden">
            {nav.map((n) => (
              <button key={n.id} onClick={() => setTab(n.id)} className={`p-2 rounded-lg ${tab === n.id ? "bg-[#F4E6DE] text-[#B4715C]" : "text-[#5A4740]"}`}>
                <n.icon className="h-5 w-5" />
              </button>
            ))}
            <button onClick={onLogout} className="p-2 text-[#C8604B]"><LogOut className="h-5 w-5" /></button>
          </div>
        </header>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-32 text-[#B4715C]">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : (
            <>
              {tab === "overview" && stats && (
                <div className="space-y-8">
                  <div className="grid grid-cols-2 lg:grid-cols-5 gap-5">
                    <StatCard icon={Euro} label="Omzet (betaald)" value={`€${stats.revenue.toFixed(2)}`} />
                    <StatCard icon={ShoppingCart} label="Betaalde orders" value={stats.orders_paid} />
                    <StatCard icon={TrendingUp} label="Verkochte items" value={stats.units_sold} />
                    <StatCard icon={Mail} label="Abonnees" value={stats.subscribers} />
                    <StatCard icon={BellRing} label="Wachtlijst" value={stats.waitlist ?? 0} />
                  </div>
                  <div className="bg-white rounded-2xl border border-[#EEDDD3] p-6">
                    <h2 className="font-serif text-xl text-[#3A2C28] mb-5">Bestverkocht</h2>
                    <div className="space-y-4">
                      {Object.keys(PRODUCT_NAMES).map((pid) => {
                        const count = stats.product_counts?.[pid] || 0;
                        const max = Math.max(1, ...Object.values(stats.product_counts || { x: 1 }));
                        return (
                          <div key={pid}>
                            <div className="flex justify-between text-sm text-[#5A4740] mb-1.5">
                              <span>{PRODUCT_NAMES[pid]}</span>
                              <span className="text-[#8A756C]">{count}</span>
                            </div>
                            <div className="h-2 rounded-full bg-[#F1E2D9] overflow-hidden">
                              <div className="h-full rounded-full bg-[#B4715C]" style={{ width: `${(count / max) * 100}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {tab === "products" && (
                <div className="grid lg:grid-cols-[1fr_1fr] gap-6">
                  {/* Add product form */}
                  <form onSubmit={submitProduct} className="bg-white rounded-2xl border border-[#EEDDD3] p-6 space-y-4 h-fit">
                    <div className="flex items-center gap-2">
                      <span className="h-9 w-9 rounded-full bg-[#F4E6DE] flex items-center justify-center text-[#B4715C]"><PackagePlus className="h-4 w-4" /></span>
                      <h2 className="font-serif text-xl text-[#3A2C28]">Nieuw product toevoegen</h2>
                    </div>
                    <div>
                      <label className="text-[11px] tracking-label uppercase text-[#8A756C]">Naam *</label>
                      <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="mt-1.5 w-full h-11 px-4 rounded-xl border border-[#E0CDC2] bg-[#FBF5F0] text-sm focus:outline-none focus:border-[#B4715C]" placeholder="bijv. Cheek Tint" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[11px] tracking-label uppercase text-[#8A756C]">Prijs (€) *</label>
                        <input required type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}
                          className="mt-1.5 w-full h-11 px-4 rounded-xl border border-[#E0CDC2] bg-[#FBF5F0] text-sm focus:outline-none focus:border-[#B4715C]" placeholder="12.95" />
                      </div>
                      <div>
                        <label className="text-[11px] tracking-label uppercase text-[#8A756C]">Categorie</label>
                        <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                          className="mt-1.5 w-full h-11 px-4 rounded-xl border border-[#E0CDC2] bg-[#FBF5F0] text-sm focus:outline-none focus:border-[#B4715C]" placeholder="Lippen" />
                      </div>
                    </div>
                    <div>
                      <label className="text-[11px] tracking-label uppercase text-[#8A756C]">Subtitel</label>
                      <input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                        className="mt-1.5 w-full h-11 px-4 rounded-xl border border-[#E0CDC2] bg-[#FBF5F0] text-sm focus:outline-none focus:border-[#B4715C]" placeholder="bijv. Natural Flush" />
                    </div>
                    <div>
                      <label className="text-[11px] tracking-label uppercase text-[#8A756C]">Omschrijving</label>
                      <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                        className="mt-1.5 w-full px-4 py-3 rounded-xl border border-[#E0CDC2] bg-[#FBF5F0] text-sm focus:outline-none focus:border-[#B4715C]" placeholder="Korte productomschrijving..." />
                    </div>
                    <div>
                      <label className="text-[11px] tracking-label uppercase text-[#8A756C]">Afbeelding URL</label>
                      <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })}
                        className="mt-1.5 w-full h-11 px-4 rounded-xl border border-[#E0CDC2] bg-[#FBF5F0] text-sm focus:outline-none focus:border-[#B4715C]" placeholder="https://..." />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-[#5A4740] cursor-pointer">
                      <input type="checkbox" checked={form.preorder} onChange={(e) => setForm({ ...form, preorder: e.target.checked })} className="h-4 w-4 accent-[#B4715C]" />
                      Pre-order product
                    </label>
                    <button type="submit" disabled={saving}
                      className="w-full h-12 rounded-full bg-[#B4715C] text-white text-[11px] tracking-label uppercase hover:bg-[#9E5F4B] disabled:opacity-70 flex items-center justify-center gap-2">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Product toevoegen
                    </button>
                    <p className="text-[11px] text-[#A38F86] text-center">Er wordt automatisch een Stripe-prijs aangemaakt.</p>
                  </form>

                  {/* Product list */}
                  <div className="bg-white rounded-2xl border border-[#EEDDD3] p-6">
                    <h2 className="font-serif text-xl text-[#3A2C28] mb-4">Toegevoegde producten ({products.length})</h2>
                    {products.length === 0 ? (
                      <p className="text-sm text-[#A38F86] py-8 text-center">Nog geen eigen producten. Voeg er links één toe.</p>
                    ) : (
                      <div className="space-y-3">
                        {products.map((p) => (
                          <div key={p.product_id} className="flex items-center gap-4 p-3 rounded-xl border border-[#F0E2D9]">
                            <div className="h-14 w-14 rounded-lg bg-[#F3E7DF] overflow-hidden shrink-0">
                              {p.image ? <img src={p.image} alt={p.name} className="h-full w-full object-cover" /> : null}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[15px] text-[#3A2C28] truncate">{p.name}</p>
                              <p className="text-[12px] text-[#8A756C]">€{(p.price || 0).toFixed(2)} · {p.category}{p.preorder ? " · pre-order" : ""}</p>
                            </div>
                            <button onClick={() => removeProduct(p.product_id)} className="text-[#B09C93] hover:text-[#C8604B] p-2" aria-label="Delete">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {tab === "orders" && (
                <div className="space-y-4">
                  <div className="relative max-w-sm">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#B09C93]" />
                    <input
                      value={orderSearch}
                      onChange={(e) => setOrderSearch(e.target.value)}
                      placeholder="Zoek op naam of e-mail..."
                      data-testid="orders-search-input"
                      className="w-full h-11 pl-11 pr-4 rounded-full border border-[#E0CDC2] bg-white text-sm focus:outline-none focus:border-[#B4715C]"
                    />
                  </div>
                  <div className="bg-white rounded-2xl border border-[#EEDDD3] overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[11px] tracking-label uppercase text-[#8A756C] border-b border-[#EEDDD3]">
                          <th className="px-5 py-4 font-medium">Datum</th>
                          <th className="px-5 py-4 font-medium">Klant</th>
                          <th className="px-5 py-4 font-medium">Producten</th>
                          <th className="px-5 py-4 font-medium">Bedrag</th>
                          <th className="px-5 py-4 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.filter((o) => matchQuery(orderSearch, o.customer?.name, o.customer?.shipping_name, o.customer?.email)).length === 0 && (
                          <tr><td colSpan={5} className="px-5 py-10 text-center text-[#A38F86]">Geen bestellingen gevonden.</td></tr>
                        )}
                        {orders.filter((o) => matchQuery(orderSearch, o.customer?.name, o.customer?.shipping_name, o.customer?.email)).map((o) => (
                          <tr key={o.session_id} onClick={() => setSelectedOrder(o)} className="border-b border-[#F3E7DF] last:border-0 align-top cursor-pointer hover:bg-[#FBF5F0]">
                            <td className="px-5 py-4 text-[#5A4740] whitespace-nowrap">{fmtDate(o.created_at)}</td>
                            <td className="px-5 py-4 text-[#5A4740]">
                              {o.customer ? (
                                <div className="space-y-0.5">
                                  <p className="text-[#3A2C28]">{o.customer.name || o.customer.shipping_name || "-"}</p>
                                  {o.delivery_method === "pickup" && (
                                    <span className="inline-block mt-1 text-[10px] tracking-label uppercase px-2 py-0.5 rounded-full bg-[#EFE6DE] text-[#8A5E48]">Afhalen Lelystad</span>
                                  )}
                                  {o.customer.email && <p className="text-[12px] text-[#8A756C]">{o.customer.email}</p>}
                                  {o.customer.phone && <p className="text-[12px] text-[#8A756C]">{o.customer.phone}</p>}
                                  {o.customer.shipping_address && (
                                    <p className="text-[12px] text-[#A08678]">
                                      {[o.customer.shipping_address.line1, o.customer.shipping_address.postal_code, o.customer.shipping_address.city, o.customer.shipping_address.country].filter(Boolean).join(", ")}
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <span className="text-[12px] text-[#C0A99E]">—</span>
                              )}
                            </td>
                            <td className="px-5 py-4 text-[#5A4740]">
                              {(o.items || []).map((it, i) => (
                                <div key={i}>
                                  {it.quantity}× {PRODUCT_NAMES[it.product_id] || it.product_id}
                                  {it.shade ? ` (${it.shade})` : ""}
                                </div>
                              ))}
                            </td>
                            <td className="px-5 py-4 text-[#3A2C28] whitespace-nowrap">€{(o.amount || 0).toFixed(2)}</td>
                            <td className="px-5 py-4">
                              <span className={`text-[11px] tracking-label uppercase px-2.5 py-1 rounded-full ${STATUS_STYLES[o.payment_status] || STATUS_STYLES.expired}`}>
                                {o.payment_status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                </div>
              )}

              {tab === "paid" && (
                <div className="space-y-5" data-testid="paid-orders-section">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white rounded-2xl border border-[#EEDDD3] p-6">
                    <div>
                      <p className="font-serif text-xl text-[#3A2C28]">Betaalde orders (live via Stripe)</p>
                      <p className="text-sm text-[#8A756C] mt-1" data-testid="paid-orders-summary">
                        {paidOrders.length} betaalde betalingen · totaal <span className="text-[#3A2C28] font-medium">€{paidTotal.toFixed(2)}</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => loadPaid(true)}
                        disabled={paidLoading}
                        className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-[#E0CDC2] text-[#5A4740] text-[11px] tracking-label uppercase hover:bg-[#FBF5F0] disabled:opacity-60"
                        data-testid="refresh-paid-orders-btn"
                      >
                        {paidLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Vernieuwen
                      </button>
                      <a
                        href="https://dashboard.stripe.com/payments"
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#635BFF] text-white text-[11px] tracking-label uppercase hover:opacity-90"
                        data-testid="open-stripe-dashboard-link"
                      >
                        <ExternalLink className="h-4 w-4" /> Open in Stripe
                      </a>
                    </div>
                  </div>
                  <div className="relative max-w-sm">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#B09C93]" />
                    <input
                      value={paidSearch}
                      onChange={(e) => setPaidSearch(e.target.value)}
                      placeholder="Zoek op naam of e-mail..."
                      data-testid="paid-search-input"
                      className="w-full h-11 pl-11 pr-4 rounded-full border border-[#E0CDC2] bg-white text-sm focus:outline-none focus:border-[#B4715C]"
                    />
                  </div>
                  <div className="bg-white rounded-2xl border border-[#EEDDD3] overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-[11px] tracking-label uppercase text-[#8A756C] border-b border-[#EEDDD3]">
                            <th className="px-5 py-4 font-medium">Datum</th>
                            <th className="px-5 py-4 font-medium">Klant</th>
                            <th className="px-5 py-4 font-medium">Methode</th>
                            <th className="px-5 py-4 font-medium">Bedrag</th>
                            <th className="px-5 py-4 font-medium">Status</th>
                            <th className="px-5 py-4 font-medium"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {paidLoading && (
                            <tr><td colSpan={6} className="px-5 py-12 text-center text-[#B4715C]"><Loader2 className="h-6 w-6 animate-spin inline" /></td></tr>
                          )}
                          {!paidLoading && paidOrders.filter((p) => matchQuery(paidSearch, p.name, p.email)).length === 0 && (
                            <tr><td colSpan={6} className="px-5 py-10 text-center text-[#A38F86]">Geen betaalde orders gevonden.</td></tr>
                          )}
                          {!paidLoading && paidOrders.filter((p) => matchQuery(paidSearch, p.name, p.email)).map((p) => (
                            <tr key={p.id} className="border-b border-[#F3E7DF] last:border-0 hover:bg-[#FBF5F0]" data-testid={`paid-order-row-${p.id}`}>
                              <td className="px-5 py-4 text-[#5A4740] whitespace-nowrap">{fmtDate(p.created)}</td>
                              <td className="px-5 py-4 text-[#5A4740]">
                                <p className="text-[#3A2C28]">{p.name || "—"}</p>
                                {p.email && <p className="text-[12px] text-[#8A756C]">{p.email}</p>}
                              </td>
                              <td className="px-5 py-4 text-[#5A4740] uppercase text-[12px]">{p.method || "—"}</td>
                              <td className="px-5 py-4 text-[#3A2C28] whitespace-nowrap">€{(p.amount || 0).toFixed(2)}</td>
                              <td className="px-5 py-4">
                                {p.refunded ? (
                                  <span className="text-[11px] tracking-label uppercase px-2.5 py-1 rounded-full bg-[#F7E0DA] text-[#B4553F]">Terugbetaald</span>
                                ) : (
                                  <span className="text-[11px] tracking-label uppercase px-2.5 py-1 rounded-full bg-[#E4F1E9] text-[#3C7A57]">Betaald</span>
                                )}
                              </td>
                              <td className="px-5 py-4 text-right">
                                {p.dashboard_url && (
                                  <a href={p.dashboard_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[12px] text-[#635BFF] hover:underline" data-testid={`stripe-link-${p.id}`}>
                                    Stripe <ExternalLink className="h-3.5 w-3.5" />
                                  </a>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {tab === "subscribers" && (
                <div className="bg-white rounded-2xl border border-[#EEDDD3] overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[11px] tracking-label uppercase text-[#8A756C] border-b border-[#EEDDD3]">
                        <th className="px-5 py-4 font-medium">E-mail</th>
                        <th className="px-5 py-4 font-medium">Ingeschreven op</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subs.length === 0 && (
                        <tr><td colSpan={2} className="px-5 py-10 text-center text-[#A38F86]">Nog geen abonnees.</td></tr>
                      )}
                      {subs.map((s) => (
                        <tr key={s.id} className="border-b border-[#F3E7DF] last:border-0">
                          <td className="px-5 py-4 text-[#3A2C28]">{s.email}</td>
                          <td className="px-5 py-4 text-[#5A4740]">{fmtDate(s.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {tab === "waitlist" && (
                <div className="space-y-5">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white rounded-2xl border border-[#EEDDD3] p-6">
                    <div>
                      <p className="font-serif text-xl text-[#3A2C28]">Pre-order wachtlijst</p>
                      <p className="text-sm text-[#8A756C] mt-1">
                        {stats?.waitlist_pending ?? 0} nog niet genotificeerd · {stats?.waitlist ?? 0} totaal
                      </p>
                    </div>
                    <button
                      onClick={notifyAll}
                      disabled={notifying || (stats?.waitlist_pending ?? 0) === 0}
                      className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-full bg-[#B4715C] text-white text-[11px] tracking-label uppercase hover:bg-[#9E5F4B] disabled:opacity-60"
                    >
                      {notifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      Notify all (mock)
                    </button>
                  </div>
                  <div className="bg-white rounded-2xl border border-[#EEDDD3] overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[11px] tracking-label uppercase text-[#8A756C] border-b border-[#EEDDD3]">
                          <th className="px-5 py-4 font-medium">E-mail</th>
                          <th className="px-5 py-4 font-medium">Ingeschreven</th>
                          <th className="px-5 py-4 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {waitlist.length === 0 && (
                          <tr><td colSpan={3} className="px-5 py-10 text-center text-[#A38F86]">Nog geen aanmeldingen.</td></tr>
                        )}
                        {waitlist.map((w) => (
                          <tr key={w.id} className="border-b border-[#F3E7DF] last:border-0">
                            <td className="px-5 py-4 text-[#3A2C28]">{w.email}</td>
                            <td className="px-5 py-4 text-[#5A4740]">{fmtDate(w.created_at)}</td>
                            <td className="px-5 py-4">
                              <span className={`text-[11px] tracking-label uppercase px-2.5 py-1 rounded-full ${w.notified ? "bg-[#E4F1E9] text-[#3C7A57]" : "bg-[#FBEFD9] text-[#9A7420]"}`}>
                                {w.notified ? "Genotificeerd" : "In afwachting"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {selectedOrder && (
        <OrderDetailModal order={selectedOrder} onClose={() => setSelectedOrder(null)} fmtDate={fmtDate} token={token} />
      )}
    </div>
  );
};

const OrderDetailModal = ({ order, onClose, fmtDate, token }) => {
  const c = order.customer || {};
  const [link, setLink] = useState(order.payment_link || "");
  const [emailed, setEmailed] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();
  const paid = order.payment_status === "paid";

  const genLink = async () => {
    setBusy(true);
    try {
      const res = await createPaymentLink(token, order.session_id);
      setLink(res.payment_link);
      setEmailed(res.emailed);
      toast({ title: "LÉLUNE", description: res.emailed ? "Betaallink aangemaakt en gemaild." : "Betaallink aangemaakt (mail gemockt/uit)." });
    } catch {
      toast({ title: "LÉLUNE", description: "Kon geen betaallink maken." });
    } finally {
      setBusy(false);
    }
  };
  const copy = () => {
    navigator.clipboard?.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const addrStr = (a) => a ? [a.line1, a.line2, a.postal_code, a.city, a.state, a.country].filter(Boolean).join(", ") : "—";

  const printInvoice = () => {
    const inv = order.invoice;
    if (!inv) return;
    const rows = (order.items || []).map((it) => {
      const nm = PRODUCT_NAMES[it.product_id] || it.product_id;
      const shade = it.shade ? ` (tint ${it.shade})` : "";
      const lt = ((it.unit_price || 0) * (it.quantity || 1)).toFixed(2);
      return `<tr><td>${it.quantity}× ${nm}${shade}</td><td style="text-align:right">€${lt}</td></tr>`;
    }).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Factuur ${inv.number}</title>
      <style>body{font-family:Arial,sans-serif;color:#3A2C28;max-width:720px;margin:32px auto;padding:0 24px}
      h1{font-family:Georgia,serif;letter-spacing:6px;font-weight:400;margin:0}
      table{width:100%;border-collapse:collapse;margin-top:16px}td{padding:8px 0;border-bottom:1px solid #eee}
      .tot td{border:0;padding:4px 0}.muted{color:#8A756C;font-size:13px}</style></head><body>
      <div style="display:flex;justify-content:space-between;align-items:flex-start">
        <div><h1>LÉLUNE</h1><p class="muted">EJ Trading · lelune.nl</p></div>
        <div style="text-align:right"><h2 style="margin:0">Factuur</h2>
        <p class="muted">${inv.number}<br>${new Date(inv.date).toLocaleDateString("nl-NL")}</p></div>
      </div>
      <div style="margin-top:16px"><p class="muted">Factuur aan:</p>
      <strong>${c.name || c.shipping_name || "-"}</strong><br>
      ${c.company ? c.company + "<br>" : ""}${addrStr(c.shipping_address || c.billing_address)}<br>${c.email || ""}</div>
      <table>${rows}</table>
      <table class="tot" style="margin-top:12px;width:280px;margin-left:auto">
        <tr><td class="muted">Subtotaal</td><td style="text-align:right">€${inv.subtotal.toFixed(2)}</td></tr>
        <tr><td class="muted">Verzending</td><td style="text-align:right">€${inv.shipping.toFixed(2)}</td></tr>
        <tr><td class="muted">Waarvan btw (${inv.vat_rate}%)</td><td style="text-align:right">€${inv.vat.toFixed(2)}</td></tr>
        <tr><td><strong>Totaal</strong></td><td style="text-align:right"><strong>€${inv.total.toFixed(2)}</strong></td></tr>
      </table>
      <p class="muted" style="margin-top:32px">Bedankt voor je bestelling bij LÉLUNE.</p>
      <script>window.onload=function(){window.print()}</script></body></html>`;
    const w = window.open("", "_blank");
    if (w) { w.document.write(html); w.document.close(); }
  };
  const Row = ({ label, value }) => (
    <div className="flex justify-between gap-4 py-2 border-b border-[#F3E7DF] last:border-0">
      <span className="text-[11px] tracking-label uppercase text-[#8A756C] shrink-0">{label}</span>
      <span className="text-sm text-[#3A2C28] text-right">{value || "—"}</span>
    </div>
  );
  return (
    <>
      <div className="fixed inset-0 z-50 bg-[#2A201C]/50 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed z-50 left-1/2 top-1/2 w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 max-h-[88vh] overflow-y-auto bg-[#FBF5F0] rounded-3xl border border-[#EEE0D7] shadow-2xl">
        <div className="flex items-center justify-between px-6 h-[64px] border-b border-[#E7D3C8] sticky top-0 bg-[#FBF5F0]">
          <h2 className="font-serif text-2xl text-[#3A2C28]">Bestelling</h2>
          <button onClick={onClose} aria-label="Close" className="text-[#8A756C] hover:text-[#B4715C]"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between">
            <span className={`text-[11px] tracking-label uppercase px-3 py-1 rounded-full ${STATUS_STYLES[order.payment_status] || STATUS_STYLES.expired}`}>
              {order.payment_status}
            </span>
            <span className="font-serif text-3xl text-[#3A2C28]">€{(order.amount || 0).toFixed(2)}</span>
          </div>

          {!paid && (
            <div className="bg-[#FBF0E9] rounded-2xl border border-[#EAD3C6] p-5">
              <p className="text-[11px] tracking-label uppercase text-[#B4715C] mb-2">Betaling openstaand</p>
              <p className="text-sm text-[#6E5A52]">Maak een nieuwe betaallink en stuur die naar de klant.</p>
              <button onClick={genLink} disabled={busy} className="mt-3 inline-flex items-center gap-2 h-11 px-6 rounded-full bg-[#B4715C] text-white text-[11px] tracking-label uppercase hover:bg-[#9E5F4B] disabled:opacity-70">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} Nieuwe betaallink
              </button>
              {link && (
                <div className="mt-4">
                  <div className="flex items-center gap-2">
                    <input readOnly value={link} className="flex-1 h-10 px-3 rounded-lg border border-[#E0CDC2] bg-white text-[12px] text-[#5A4740]" />
                    <button onClick={copy} className="h-10 w-10 rounded-lg bg-[#3A2C28] text-white flex items-center justify-center hover:bg-[#B4715C]" aria-label="Copy">
                      {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="mt-2 text-[11px] text-[#8A756C]">
                    {emailed === true && "Automatisch gemaild naar de klant."}
                    {emailed === false && "Mail niet verstuurd (verifieer SendGrid-afzender) — kopieer de link handmatig."}
                    {emailed === null && "Deel deze link met de klant."}
                  </p>
                </div>
              )}
            </div>
          )}

          {order.invoice && (
            <div className="bg-white rounded-2xl border border-[#EEE0D7] p-5 flex items-center justify-between">
              <div>
                <p className="text-[11px] tracking-label uppercase text-[#B4715C]">Factuur</p>
                <p className="font-serif text-xl text-[#3A2C28]">{order.invoice.number}</p>
                <p className="text-[12px] text-[#8A756C]">Totaal €{order.invoice.total.toFixed(2)} · incl. €{order.invoice.vat.toFixed(2)} btw</p>
              </div>
              <button onClick={printInvoice} className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#3A2C28] text-white text-[11px] tracking-label uppercase hover:bg-[#B4715C]">
                Factuur bekijken
              </button>
            </div>
          )}

          <div>
            <p className="text-[11px] tracking-label uppercase text-[#B4715C] mb-2">Producten</p>
            <div className="bg-white rounded-xl border border-[#EEE0D7] divide-y divide-[#F3E7DF]">
              {(order.items || []).map((it, i) => (
                <div key={i} className="flex justify-between px-4 py-3 text-sm">
                  <span className="text-[#3A2C28]">{it.quantity}× {PRODUCT_NAMES[it.product_id] || it.product_id}{it.shade ? ` (tint ${it.shade})` : ""}</span>
                  <span className="text-[#8A756C]">€{(it.unit_price || 0).toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="text-[11px] tracking-label uppercase text-[#B4715C] mb-1">Klantgegevens</p>
            <div className="bg-white rounded-xl border border-[#EEE0D7] px-4">
              <Row label="Naam" value={c.name || c.shipping_name} />
              <Row label="E-mail" value={c.email} />
              <Row label="Telefoon" value={c.phone} />
            </div>
          </div>

          <div>
            <p className="text-[11px] tracking-label uppercase text-[#B4715C] mb-1">Verzendadres</p>
            <div className="bg-white rounded-xl border border-[#EEE0D7] px-4">
              <Row label="T.a.v." value={c.shipping_name || c.name} />
              <Row label="Adres" value={addrStr(c.shipping_address)} />
            </div>
          </div>

          <div>
            <p className="text-[11px] tracking-label uppercase text-[#B4715C] mb-1">Factuuradres</p>
            <div className="bg-white rounded-xl border border-[#EEE0D7] px-4">
              <Row label="Adres" value={addrStr(c.billing_address)} />
            </div>
          </div>

          {c.note && (
            <div>
              <p className="text-[11px] tracking-label uppercase text-[#B4715C] mb-1">Opmerking</p>
              <p className="bg-white rounded-xl border border-[#EEE0D7] px-4 py-3 text-sm text-[#3A2C28]">{c.note}</p>
            </div>
          )}

          <div className="pt-2">
            <Row label="Datum" value={fmtDate(order.created_at)} />
            <Row label="Sessie" value={order.session_id} />
          </div>

          {!order.customer && (
            <p className="text-[12px] text-[#A38F86] text-center">Klantgegevens verschijnen zodra de betaling is voltooid.</p>
          )}
        </div>
      </div>
    </>
  );
};

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem("lelune_admin"));
  const logout = () => {
    localStorage.removeItem("lelune_admin");
    setToken(null);
  };
  if (!token) return <Login onLogin={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
};

export default Admin;
